import { ApiBaseService, DataServiceAPI, ApiV2Service } from '@/services/api.base.service';
import { SuccessResponse, Widget } from '../types';

export class WidgetService {
  private api: ApiBaseService;
  private dataService: ApiBaseService;

  constructor() {
    this.api = ApiV2Service;
    this.dataService = DataServiceAPI;
  }

  public async getWidgets(): Promise<Widget[]> {
    const widgets = await this.api.get<Widget[]>(`dashboard/widgets`);
    if (widgets.length === 0) {
      return this.createDefaultWidgets();
    } else {
      const promises = [];
      for (const widget of widgets) {
        let endpoint = widget.dataEndpoint;
        if (widget.dataEndpoint.charAt(0) === '/') { // Remove when endpoint has beginning / removed.
          endpoint = widget.dataEndpoint.replace(/^\/|\/$/g, '');
        }

        widget.payload = {};

        if (widget.source === 'map') {
          const sitesPromise = this.getSites().then((sites) => {
            widget.payload.sites = [];
            Object.assign(widget.payload.sites, sites);
          });

          const devicesPromise = this.getDevices().then((devices) => {
            widget.payload.devices = [];
            Object.assign(widget.payload.devices, devices);
          });

          const projectsPromise = this.getProjects().then((projects) => {
            widget.payload.projects = [];
            Object.assign(widget.payload.projects, projects);
          });

          promises.push(sitesPromise, devicesPromise, projectsPromise);
        } else if (widget.source === 'table') { // This could be improved.
          if (widget.title === 'projects') {
            const projectsPromise = this.getProjects().then((projects) => {
              widget.payload.headers = [];
              for (const field in projects[0]) {
                if (projects[0].hasOwnProperty(field)) {
                  widget.payload.headers.push({value: field, text: field});
                }
              }
              Object.assign(widget.payload, {values: projects});
            });

            promises.push(projectsPromise);
          }
        } else if (widget.source === 'events') {
          const eventsPromise = this.getEvents(endpoint + '?period=1w&limit=100').then((events) => {// TODO: Get from widget config
            widget.payload.headers = [];
            for (const field in events[0]) {
              if (events[0].hasOwnProperty(field)) {
                widget.payload.headers.push({value: field, text: field});
              }
            }
            Object.assign(widget.payload, {values: events});
          });

          promises.push(eventsPromise);
        } else if (widget.source === 'chart') {
          const deviceVersionsPromise = this.getDeviceTypes().then((deviceVersions) => {
            Object.assign(widget.payload, {values: deviceVersions});
          }); // TODO: Get from widget config

          promises.push(deviceVersionsPromise);
        }
      }

      await Promise.all(promises);
      return widgets;
    }
  }

  public async updateWidget(widgetId: string, payload: object): Promise<Widget> {
    return this.api.put(`dashboard/widgets/${widgetId}`, payload);
  }

  public async getMeasurements(organisation: string, device: string, field: string, chartType: string, groupBy: string): Promise<any> {
    let queryString = '?';
    queryString += [ 'line', 'area' ].includes( chartType ) ? `&chartType=series` : '';
    queryString += groupBy ? `&groupBy=${groupBy}` : '';
    return await this.dataService.get(`measurements/${organisation}/${device}/${field}${queryString}`);
  }

  public async removeWidget(widgetId: string): Promise<SuccessResponse> {
    return await this.api.delete(`dashboard/widgets/${widgetId}`);
  }

  public async createDefaultWidgets(): Promise<Widget[]> {
    const defaultWidgets = [{
      source: 'table',
      dataEndpoint: 'dashboard/projects',
      title: 'projects',
      x: 0,
      y: 0,
      width: 3,
      height: 1.5,
      enabledFields: [ 'organisationName', 'projectName', 'groupName', 'status', 'deviceActiveCount', 'deviceFaultCount', 'actions' ],
      config: {},
    },
    {
      source: 'map',
      dataEndpoint: 'dashboard/sites',
      title: 'sites',
      x: 3,
      y: 0,
      width: 3,
      height: 1.5,
      enabledFields: [],
      config: {},
    },
    {
      source: 'events',
      dataEndpoint: 'dashboard/events',
      title: 'events',
      x: 0,
      y: 1.5,
      width: 6,
      height: 3,
      enabledFields: [ 'deviceNetworkId', 'eventType', 'name', 'projectName', 'eventMessage', 'eventSeverity', 'eventTime', 'actions' ],
      config: {
        organisationFilters: [],
        projectFilters: [],
      },
    },
    /*{
      source: 'table',
      dataEndpoint: 'dashboard/devices',
      title: 'devices',
      x: 2,
      y: 1.4,
      width: 2,
      height: 1.4,
      enabledFields: [ 'deviceId', 'networkId', 'deviceType', 'siteName', 'locationName' ],
      config: {},
    },
    {
      source: 'chart',
      dataEndpoint: 'dashboard/deviceVersions',
      title: 'Measurements',
      x: 4,
      y: 1.4,
      width: 2,
      height: 1.4,
      enabledFields: [],
      config: {},
    }*/];

    await Promise.all(defaultWidgets.map(async (widget: any): Promise<any> => await this.api.post(`dashboard/widgets`, widget)));

    return await this.getWidgets();
  }

  public async getEventsCount(projectName: string): Promise<any> {
    return this.api.get(`dashboard/eventsCount?project=${projectName}`);
  }

  public async getShadows(): Promise<any[]> {
    return this.api.get(`dashboard/devices/shadows`);

    // return [
    //   {
    //     device: {
    //       slug_id: 'refrigerationappdjdcr_162',
    //       position_id: 1390,
    //       id: 15873,
    //       deviceId: 'POSITION_4',
    //       deviceName: 'A81758FDFEA49EF1',
    //       status: 'inactive',
    //       statusId: 3,
    //       versionId: 20,
    //       deviceType: 'Position',
    //       projectName: 'TestForApp',
    //       organisationId: 1,
    //       locationName: 'Monitoring Point 0',
    //       lat: 50.723976,
    //       lng: -1.894256,
    //       networkId: 'A81758FDFEA49EF1'
    //     },
    //     shadow: {
    //       deviceId: 'A81758FDFEA49EF1',
    //       projectId: 162,
    //       positionId: 1390,
    //       timestamp: 1675957608641,
    //       computed: {},
    //       device: {
    //         faultFlag: false,
    //         resetFlag: false,
    //         messageType: 1,
    //         requestForServerTime: false,
    //         alarmMessageCount: 0,
    //         backlogMessageCount: 0,
    //         temperature: -17,
    //         humidity: 40,
    //         batteryPercentage: '80-100%',
    //         configurationError: false,
    //         alarmFlag: false
    //       },
    //       network: {
    //         rssi: -58,
    //         fPort: 1,
    //         lng: 0,
    //         data: {
    //           data: [
    //             1,
    //             0,
    //             0,
    //             40,
    //             0,
    //             239,
    //             5,
    //             0,
    //             0,
    //             0,
    //             0
    //           ],
    //           type: Buffer
    //         },
    //         snr: 7.2,
    //         station: '7276ff0039000000',
    //         bearer: 'lorawan',
    //         seqNumber: 1760,
    //         lat: 0
    //       },
    //       interfaces: {
    //         ambienttemperature: {
    //           temperature: -17,
    //           type: 'measurement',
    //           precision: 0.01
    //         },
    //         ambienthumidity: {
    //           type: 'measurement',
    //           humidity: 40,
    //           precision: 0.01
    //         }
    //       }
    //     }
    //   },
    //   {
    //     device: {
    //       slug_id: 'refrigerationappdjdcr_162',
    //       position_id: 1375,
    //       id: 15872,
    //       deviceId: 'POSITION_3',
    //       deviceName: '0025CA0A00011614',
    //       status: 'inactive',
    //       statusId: 3,
    //       versionId: 20,
    //       deviceType: 'Position',
    //       projectName: 'TestForApp',
    //       organisationId: 18241,
    //       locationName: 'Monitoring Point 1',
    //       lat: 50.723976,
    //       lng: -1.894256,
    //       networkId: '0025CA0A00011614'
    //     },
    //     shadow: {
    //       deviceId: '0025CA0A00011614',
    //       projectId: 162,
    //       positionId: 1375,
    //       timestamp: 1675958028307,
    //       computed: {},
    //       device: {
    //         date: 11/6/21,
    //         resetFlag: false,
    //         configurationError: false,
    //         alarmFlag: false,
    //         faultFlag: false,
    //         messageType: 1,
    //         requestForServerTime: false,
    //         alarmMessageCount: 0,
    //         backlogMessageCount: 0,
    //         temperature: 4.38,
    //         humidity: 87.57,
    //         batteryPercentage: '80-100%',
    //         partNumber: 4800114,
    //         firmwareVersion: 7.0
    //       },
    //       network: {
    //         loraBandwidth: 125000,
    //         loraSpreadingFactor: 7,
    //         rssi: -63,
    //         fPort: 1,
    //         data: 'AQE5VyYEBQAAAAA=',
    //         snr: 8,
    //         station: 'eui-c0ee40ffff2a0fd6',
    //         loraCodingRate: 4/5,
    //         networkTimestamp: 1675958028307,
    //         bearer: 'lorawan',
    //         seqNumber: 96
    //       },
    //       interfaces: {
    //         ambienttemperature: {
    //           temperature: 4.38,
    //           type: 'measurement',
    //           precision: 0.01
    //         },
    //         ambienthumidity: {
    //           type: 'measurement',
    //           humidity: 87.57,
    //           precision: 0.01
    //         }
    //       }
    //     }
    //   },
    //   {
    //     device: {
    //       slug_id: 'refrigerationappdjdcr_162',
    //       position_id: 1376,
    //       id: 15873,
    //       deviceId: 'POSITION_3',
    //       deviceName: '0025CA0A00011615',
    //       status: 'inactive',
    //       statusId: 3,
    //       versionId: 20,
    //       deviceType: 'Position',
    //       projectName: 'TestForApp',
    //       organisationId: 18241,
    //       locationName: 'Monitoring Point 2',
    //       lat: 50.723976,
    //       lng: -1.894256,
    //       networkId: '0025CA0A00011615'
    //     },
    //     shadow: {
    //       deviceId: '0025CA0A00011615',
    //       projectId: 162,
    //       positionId: 1375,
    //       timestamp: 1675958028307,
    //       computed: {},
    //       device: {
    //         date: 11/6/21,
    //         resetFlag: false,
    //         configurationError: false,
    //         alarmFlag: false,
    //         faultFlag: false,
    //         messageType: 1,
    //         requestForServerTime: false,
    //         alarmMessageCount: 0,
    //         backlogMessageCount: 0,
    //         temperature: 4.38,
    //         humidity: 87.57,
    //         batteryPercentage: '80-100%',
    //         partNumber: 4800114,
    //         firmwareVersion: 7.0
    //       },
    //       network: {
    //         loraBandwidth: 125000,
    //         loraSpreadingFactor: 9,
    //         rssi: -63,
    //         fPort: 1,
    //         data: 'AQE5VyYEBQAAAAA=',
    //         snr: 8,
    //         station: 'eui-c0ee40ffff2a0fd6',
    //         loraCodingRate: 4/5,
    //         networkTimestamp: 1675958028307,
    //         bearer: 'lorawan',
    //         seqNumber: 96
    //       },
    //       interfaces: {
    //         ambienttemperature: {
    //           temperature: 4.38,
    //           type: 'measurement',
    //           precision: 0.01
    //         },
    //         ambienthumidity: {
    //           type: 'measurement',
    //           humidity: 87.57,
    //           precision: 0.01
    //         }
    //       }
    //     }
    //   },
    //   {
    //     device: {
    //       slug_id: 'refrigerationappdjdcr_162',
    //       position_id: 1377,
    //       id: 15874,
    //       deviceId: 'POSITION_3',
    //       deviceName: '0025CA0A00011616',
    //       status: 'inactive',
    //       statusId: 3,
    //       versionId: 20,
    //       deviceType: 'Position',
    //       projectName: 'TestForApp',
    //       organisationId: 1,
    //       locationName: 'Monitoring Point 3',
    //       lat: 50.77031230705972,
    //       lng: -1.9015069893518044,
    //       networkId: '0025CA0A00011616'
    //     },
    //     shadow: {
    //       deviceId: '0025CA0A00011616',
    //       projectId: 162,
    //       positionId: 1375,
    //       timestamp: 1675958028307,
    //       computed: {},
    //       device: {
    //         date: 11/6/21,
    //         resetFlag: false,
    //         configurationError: false,
    //         alarmFlag: false,
    //         faultFlag: false,
    //         messageType: 1,
    //         requestForServerTime: false,
    //         alarmMessageCount: 0,
    //         backlogMessageCount: 0,
    //         temperature: 4.38,
    //         humidity: 87.57,
    //         batteryPercentage: '80-100%',
    //         partNumber: 4800114,
    //         firmwareVersion: 7.0
    //       },
    //       network: {
    //         loraBandwidth: 125000,
    //         rssi: -63,
    //         fPort: 1,
    //         data: 'AQE5VyYEBQAAAAA=',
    //         station: 'eui-c0ee40ffff2a0fd6',
    //         loraCodingRate: 4/5,
    //         networkTimestamp: 1675958028307,
    //         bearer: 'lorawan',
    //         seqNumber: 96
    //       },
    //       interfaces: {
    //         ambienttemperature: {
    //           temperature: 4.38,
    //           type: 'measurement',
    //           precision: 0.01
    //         },
    //         ambienthumidity: {
    //           type: 'measurement',
    //           humidity: 87.57,
    //           precision: 0.01
    //         }
    //       }
    //     }
    //   },
    //   {
    //     device: {
    //       slug_id: 'refrigerationappdjdcr_162',
    //       position_id: 1378,
    //       id: 15875,
    //       deviceId: 'POSITION_3',
    //       deviceName: '0025CA0A00011618',
    //       status: 'inactive',
    //       statusId: 3,
    //       versionId: 20,
    //       deviceType: 'Position',
    //       projectName: 'TestForApp',
    //       organisationId: 18241,
    //       locationName: 'Monitoring Point 4',
    //       lat: 50.74623194052893,
    //       lng: -1.9517898559570312,
    //       networkId: '0025CA0A00011618'
    //     },
    //     shadow: {
    //       deviceId: '0025CA0A00011618',
    //       projectId: 162,
    //       positionId: 1375,
    //       timestamp: 1675958028307,
    //       computed: {},
    //       device: {
    //         date: 11/6/21,
    //         resetFlag: false,
    //         configurationError: false,
    //         alarmFlag: false,
    //         faultFlag: false,
    //         messageType: 1,
    //         requestForServerTime: false,
    //         alarmMessageCount: 0,
    //         backlogMessageCount: 0,
    //         temperature: 4.38,
    //         humidity: 87.57,
    //         batteryPercentage: '80-100%',
    //         partNumber: 4800114,
    //         firmwareVersion: 7.0
    //       },
    //       network: {
    //         loraBandwidth: 125000,
    //         fPort: 1,
    //         data: 'AQE5VyYEBQAAAAA=',
    //         snr: 8,
    //         station: 'eui-c0ee40ffff2a0fd6',
    //         loraCodingRate: 4/5,
    //         networkTimestamp: 1675958028307,
    //         bearer: 'lorawan',
    //         seqNumber: 96
    //       },
    //       interfaces: {
    //         ambienttemperature: {
    //           temperature: 4.38,
    //           type: 'measurement',
    //           precision: 0.01
    //         },
    //         ambienthumidity: {
    //           type: 'measurement',
    //           humidity: 87.57,
    //           precision: 0.01
    //         }
    //       }
    //     }
    //   },
    //   {
    //     device: {
    //       slug_id: 'refrigerationappdjdcr_162',
    //       position_id: 1379,
    //       id: 15876,
    //       deviceId: 'POSITION_3',
    //       deviceName: '0025CA0A00011619',
    //       status: 'inactive',
    //       statusId: 3,
    //       versionId: 20,
    //       deviceType: 'Position',
    //       projectName: 'TestForApp',
    //       organisationId: 2,
    //       locationName: 'Monitoring Point 5',
    //       lat: 50.72726472937454,
    //       lng: -1.972697675228119,
    //       networkId: '0025CA0A00011619'
    //     },
    //     shadow: {
    //       deviceId: '0025CA0A00011619',
    //       projectId: 1,
    //       positionId: 1375,
    //       timestamp: 1675958028307,
    //       computed: {},
    //       device: {
    //         date: 11/6/21,
    //         resetFlag: false,
    //         configurationError: false,
    //         alarmFlag: false,
    //         faultFlag: false,
    //         messageType: 1,
    //         requestForServerTime: false,
    //         alarmMessageCount: 0,
    //         backlogMessageCount: 0,
    //         temperature: 4.38,
    //         humidity: 87.57,
    //         batteryPercentage: '80-100%',
    //         partNumber: 4800114,
    //         firmwareVersion: 7.0
    //       },
    //       network: {
    //         loraBandwidth: 125000,
    //         loraSpreadingFactor: 10,
    //         rssi: -63,
    //         fPort: 1,
    //         data: 'AQE5VyYEBQAAAAA=',
    //         snr: 8,
    //         station: 'eui-c0ee40ffff2a0fd6',
    //         loraCodingRate: 4/5,
    //         networkTimestamp: 1675958028307,
    //         bearer: 'lorawan',
    //         seqNumber: 96
    //       },
    //       interfaces: {
    //         ambienttemperature: {
    //           temperature: 4.38,
    //           type: 'measurement',
    //           precision: 0.01
    //         },
    //         ambienthumidity: {
    //           type: 'measurement',
    //           humidity: 87.57,
    //           precision: 0.01
    //         }
    //       }
    //     }
    //   },
    //   {
    //     device: {
    //       slug_id: 'refrigerationappdjdcr_162',
    //       position_id: 1380,
    //       id: 15877,
    //       deviceId: 'POSITION_3',
    //       deviceName: '0025CA0A00011620',
    //       status: 'inactive',
    //       statusId: 3,
    //       versionId: 20,
    //       deviceType: 'Position',
    //       projectName: 'TestForApp',
    //       organisationId: 18241,
    //       locationName: 'Monitoring Point 6',
    //       lat: 50.71432176161254,
    //       lng: -1.8930119276046753,
    //       networkId: '0025CA0A00011620'
    //     },
    //     shadow: {
    //       deviceId: '0025CA0A00011620',
    //       projectId: 162,
    //       positionId: 1375,
    //       timestamp: 1675958028307,
    //       computed: {},
    //       device: {
    //         date: 11/6/21,
    //         resetFlag: false,
    //         configurationError: false,
    //         alarmFlag: false,
    //         faultFlag: false,
    //         messageType: 1,
    //         requestForServerTime: false,
    //         alarmMessageCount: 0,
    //         backlogMessageCount: 0,
    //         temperature: 4.38,
    //         humidity: 87.57,
    //         batteryPercentage: '80-100%',
    //         partNumber: 4800114,
    //         firmwareVersion: 7.0
    //       },
    //       network: {
    //         loraBandwidth: 125000,
    //         loraSpreadingFactor: 8,
    //         rssi: -63,
    //         fPort: 1,
    //         data: 'AQE5VyYEBQAAAAA=',
    //         snr: 8,
    //         station: 'eui-c0ee40ffff2a0fd6',
    //         loraCodingRate: 4/5,
    //         networkTimestamp: 1675958028307,
    //         bearer: 'lorawan',
    //         seqNumber: 96
    //       },
    //       interfaces: {
    //         ambienttemperature: {
    //           temperature: 4.38,
    //           type: 'measurement',
    //           precision: 0.01
    //         },
    //         ambienthumidity: {
    //           type: 'measurement',
    //           humidity: 87.57,
    //           precision: 0.01
    //         }
    //       }
    //     }
    //   },
    //   {
    //     device: {
    //       slug_id: 'refrigerationappdjdcr_162',
    //       position_id: 1381,
    //       id: 15878,
    //       deviceId: 'POSITION_3',
    //       deviceName: '0025CA0A00011621',
    //       status: 'inactive',
    //       statusId: 3,
    //       versionId: 20,
    //       deviceType: 'Position',
    //       projectName: 'TestForApp',
    //       organisationId: 1,
    //       locationName: 'Monitoring Point 7',
    //       lat: 50.761352381794474,
    //       lng: -1.8414786458015442,
    //       networkId: '0025CA0A00011621'
    //     },
    //     shadow: {
    //       deviceId: '0025CA0A00011621',
    //       projectId: 162,
    //       positionId: 1375,
    //       timestamp: 1675958028307,
    //       computed: {},
    //       device: {
    //         date: 11/6/21,
    //         resetFlag: false,
    //         configurationError: false,
    //         alarmFlag: false,
    //         faultFlag: false,
    //         messageType: 1,
    //         requestForServerTime: false,
    //         alarmMessageCount: 0,
    //         backlogMessageCount: 0,
    //         temperature: 4.38,
    //         humidity: 87.57,
    //         batteryPercentage: '80-100%',
    //         partNumber: 4800114,
    //         firmwareVersion: 7.0
    //       },
    //       network: {
    //         loraBandwidth: 125000,
    //         loraSpreadingFactor: 12,
    //         rssi: -63,
    //         fPort: 1,
    //         data: 'AQE5VyYEBQAAAAA=',
    //         snr: 8,
    //         station: 'eui-c0ee40ffff2a0fd6',
    //         loraCodingRate: 4/5,
    //         networkTimestamp: 1675958028307,
    //         bearer: 'lorawan',
    //         seqNumber: 96
    //       },
    //       interfaces: {
    //         ambienttemperature: {
    //           temperature: 4.38,
    //           type: 'measurement',
    //           precision: 0.01
    //         },
    //         ambienthumidity: {
    //           type: 'measurement',
    //           humidity: 87.57,
    //           precision: 0.01
    //         }
    //       }
    //     }
    //   },
    //   {
    //     device: {
    //       slug_id: 'refrigerationappdjdcr_162',
    //       position_id: 1383,
    //       id: 15879,
    //       deviceId: 'POSITION_3',
    //       deviceName: '0025CA0A00011622',
    //       status: 'inactive',
    //       statusId: 3,
    //       versionId: 20,
    //       deviceType: 'Position',
    //       projectName: 'TestForApp',
    //       organisationId: 18241,
    //       locationName: 'Monitoring Point 8',
    //       lat: 50.75068950429875,
    //       lng: -1.771371066570282,
    //       networkId: '0025CA0A00011622'
    //     },
    //     shadow: {
    //       deviceId: '0025CA0A00011622',
    //       projectId: 162,
    //       positionId: 1375,
    //       timestamp: 1675958028307,
    //       computed: {},
    //       device: {
    //         date: 11/6/21,
    //         resetFlag: false,
    //         configurationError: false,
    //         alarmFlag: false,
    //         faultFlag: false,
    //         messageType: 1,
    //         requestForServerTime: false,
    //         alarmMessageCount: 0,
    //         backlogMessageCount: 0,
    //         temperature: 4.38,
    //         humidity: 87.57,
    //         batteryPercentage: '80-100%',
    //         partNumber: 4800114,
    //         firmwareVersion: 7.0
    //       },
    //       network: {
    //         loraBandwidth: 125000,
    //         loraSpreadingFactor: 9,
    //         rssi: -63,
    //         fPort: 1,
    //         data: 'AQE5VyYEBQAAAAA=',
    //         snr: 8,
    //         station: 'eui-c0ee40ffff2a0fd6',
    //         loraCodingRate: 4/5,
    //         networkTimestamp: 1675958028307,
    //         bearer: 'lorawan',
    //         seqNumber: 96
    //       },
    //       interfaces: {
    //         ambienttemperature: {
    //           temperature: 4.38,
    //           type: 'measurement',
    //           precision: 0.01
    //         },
    //         ambienthumidity: {
    //           type: 'measurement',
    //           humidity: 87.57,
    //           precision: 0.01
    //         }
    //       }
    //     }
    //   },
    //   {
    //     device: {
    //       slug_id: 'refrigerationappdjdcr_162',
    //       position_id: 1384,
    //       id: 15880,
    //       deviceId: 'POSITION_3',
    //       deviceName: '0025CA0A00011623',
    //       status: 'inactive',
    //       statusId: 3,
    //       versionId: 20,
    //       deviceType: 'Position',
    //       projectName: 'TestForApp',
    //       organisationId: 18241,
    //       locationName: 'Monitoring Point 9',
    //       lat: 50.755253,
    //       lng: -1.848644,
    //       networkId: '0025CA0A00011623'
    //     },
    //     shadow: {
    //       deviceId: '0025CA0A00011623',
    //       projectId: 162,
    //       positionId: 1375,
    //       timestamp: 1675958028307,
    //       computed: {},
    //       device: {
    //         date: 11/6/21,
    //         resetFlag: false,
    //         configurationError: false,
    //         alarmFlag: false,
    //         faultFlag: false,
    //         messageType: 1,
    //         requestForServerTime: false,
    //         alarmMessageCount: 0,
    //         backlogMessageCount: 0,
    //         temperature: 4.38,
    //         humidity: 87.57,
    //         batteryPercentage: '80-100%',
    //         partNumber: 4800114,
    //         firmwareVersion: 7.0
    //       },
    //       network: {
    //         loraBandwidth: 125000,
    //         loraSpreadingFactor: 11,
    //         rssi: -63,
    //         fPort: 1,
    //         data: 'AQE5VyYEBQAAAAA=',
    //         snr: 8,
    //         station: 'eui-c0ee40ffff2a0fd6',
    //         loraCodingRate: 4/5,
    //         networkTimestamp: 1675958028307,
    //         bearer: 'lorawan',
    //         seqNumber: 96
    //       },
    //       interfaces: {
    //         ambienttemperature: {
    //           temperature: 4.38,
    //           type: 'measurement',
    //           precision: 0.01
    //         },
    //         ambienthumidity: {
    //           type: 'measurement',
    //           humidity: 87.57,
    //           precision: 0.01
    //         }
    //       }
    //     }
    //   }
    // ]
  }

  public async getGateways(): Promise<any[]> {
    return this.api.get(`dashboard/gateways`);
  }

  public async hasTTISettings(): Promise<{ result: boolean }> {
    return this.api.get('dashboard/hasTTISettings');
  }

  private async getDevices(): Promise<any> {
    return this.api.get(`dashboard/devices`);
  }

  private async getProjects(): Promise<any> {
    return this.api.get('dashboard/projects');
  }

  private async getEvents(endpoint: string): Promise<any> {
    return this.api.get(endpoint);
  }

  private async getDeviceTypes(): Promise<any> {
    return this.api.get('dashboard/deviceVersions');
  }

  private async getSites(): Promise<any> {
    return this.api.get('dashboard/sites');
  }
}

export default new WidgetService();
